import { apiFetch } from "./apiClient";

/**
 * How a bill or summary shows its tax, one setting per account (Tax & Service
 * Settings -> Bill Display). Every screen that prints or summarises a bill
 * follows it:
 *   ITEM     tax shown on each line, no consolidated summary
 *   SUMMARY  one consolidated tax summary, no per-line tax
 *   BOTH     per-line tax and the consolidated summary
 */
export type TaxDisplayMode = "ITEM" | "SUMMARY" | "BOTH";

export const DEFAULT_TAX_DISPLAY_MODE: TaxDisplayMode = "BOTH";

// The mode of the account being worked in, kept here so the plain (non-React)
// bill builders — quotation HTML / PDF / e-mail, the tax invoice — can follow
// the setting without every caller threading it through. useTaxDisplayMode keeps
// it current; until the setting loads it is the default, so a bill is never blank.
let currentMode: TaxDisplayMode = DEFAULT_TAX_DISPLAY_MODE;
export const getCurrentTaxDisplayMode = (): TaxDisplayMode => currentMode;
export const setCurrentTaxDisplayMode = (mode: TaxDisplayMode): void => {
  currentMode = mode;
};

export const TAX_DISPLAY_OPTIONS: {
  mode: TaxDisplayMode;
  label: string;
  description: string;
}[] = [
  {
    mode: "ITEM",
    label: "Show tax per item",
    description: "Each line of the bill carries its own tax. No consolidated tax summary.",
  },
  {
    mode: "SUMMARY",
    label: "Show consolidated tax summary",
    description: "Lines show amounts only; tax appears once, as a summary by rate.",
  },
  {
    mode: "BOTH",
    label: "Show both",
    description: "Tax on each line, plus the consolidated tax summary.",
  },
];

/** Tax is printed on each line of the bill. */
export const showsItemTax = (mode: TaxDisplayMode): boolean => mode !== "SUMMARY";
/** The consolidated tax summary (by rate) is printed. */
export const showsTaxSummary = (mode: TaxDisplayMode): boolean => mode !== "ITEM";

const isMode = (v: unknown): v is TaxDisplayMode => v === "ITEM" || v === "SUMMARY" || v === "BOTH";

export const getTaxDisplayMode = async (accountId?: number): Promise<TaxDisplayMode> => {
  const res = await apiFetch(`/taxdisplay${accountId ? `?accountId=${accountId}` : ""}`);
  const body = await res.json();
  return isMode(body?.mode) ? body.mode : DEFAULT_TAX_DISPLAY_MODE;
};

export const saveTaxDisplayMode = async (
  mode: TaxDisplayMode,
  accountId?: number
): Promise<TaxDisplayMode> => {
  const res = await apiFetch("/taxdisplay", {
    method: "PUT",
    body: JSON.stringify({ accountId: accountId || null, mode }),
  });
  const body = await res.json();
  return isMode(body?.mode) ? body.mode : mode;
};
